import { createContext, useContext, useState, useEffect } from "react";
import { User } from "../types/auth";

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem("auth_user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  useEffect(() => {
    const checkTokenValidity = () => {
      const token = localStorage.getItem("decoded_token");
      if (!token) return;
  
      try {
        const parsedToken = JSON.parse(token);
        const currentTime = Date.now() / 1000;
  
        if (parsedToken.exp < currentTime) {
          logout();
        }
      } catch (error) {
        console.error("Error parsing token:", error);
        logout();
      }
    };
  
    checkTokenValidity(); // Run once on mount
  
    const interval = setInterval(checkTokenValidity, 60000); // Check every minute
  
    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  const login = (userData: User) => {
    const allowedRoles = ["admin", "clinic", "medicalcamp", "lab"];
    if (!allowedRoles.includes(userData.role.toLowerCase())) {
      console.error("Unauthorized role:", userData.role);
      throw new Error("Unauthorized role");
    }

    setUser(userData);
    localStorage.setItem("auth_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("auth_user");
    localStorage.removeItem("decoded_token");
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
